import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { 
  IconTrophy, 
  IconFilter,
  IconChevronRight,
  IconBusinessplan,
  IconCash
} from "@tabler/icons-react";
import { listBrokers, getBrokerPerformance } from "@/features/brokers/api";
import { cn } from "@/lib/utils/cn";

export function AgencyReportsPage() {
  const [selectedBrokerId, setSelectedBrokerId] = useState<string | null>(null);

  const { data: brokers, isLoading: isBrokersLoading } = useQuery({
    queryKey: ["agency-brokers-performance-list"],
    queryFn: listBrokers,
  });

  const { data: performance, isLoading: isPerformanceLoading } = useQuery({
    queryKey: ["broker-performance", selectedBrokerId],
    queryFn: () => getBrokerPerformance(selectedBrokerId!),
    enabled: !!selectedBrokerId,
  });

  if (isBrokersLoading || !brokers) {
    return <div className="p-8 text-center text-gray-500">Carregando relatórios...</div>;
  }

  const formatter = new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  });

  // Sort brokers by production for the ranking
  const rankedBrokers = [...brokers].sort((a, b) => b.productionVgv - a.productionVgv);

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-2">
        <h2 className="text-2xl font-bold tracking-tight text-ink">Relatórios de Performance</h2>
        <p className="text-ink-muted">
          Analise a produtividade individual e o ranking da sua imobiliária.
        </p>
      </div>

      <div className="grid gap-8 lg:grid-cols-3">
        {/* RANKING / SELECTION */}
        <div className="lg:col-span-1 space-y-4">
          <div className="rounded-3xl border border-white/40 bg-white/70 shadow-card backdrop-blur-sm overflow-hidden">
            <div className="border-b border-border-soft bg-gray-50/30 px-6 py-5 flex items-center justify-between">
              <h3 className="font-bold text-ink flex items-center gap-2">
                <IconTrophy size={20} className="text-amber-500" />
                Ranking de Produção
              </h3>
            </div>
            {rankedBrokers.every((b) => b.productionVgv === 0) && (
              <p className="px-6 py-4 text-xs text-ink-muted">
                Ninguém da equipe registrou produção nos últimos 30 dias.
              </p>
            )}
            <div className="divide-y divide-border-soft max-h-[600px] overflow-y-auto">
              {rankedBrokers.map((broker, index) => (
                <button
                  key={broker.id}
                  onClick={() => setSelectedBrokerId(broker.id)}
                  className={cn(
                    "w-full flex items-center gap-4 px-6 py-5 text-left transition-all hover:bg-white/50",
                    selectedBrokerId === broker.id ? "bg-brand-grad-soft border-l-4 border-l-brand-600" : ""
                  )}
                >
                  <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-gray-100 text-[10px] font-black text-ink-soft shadow-inner-light">
                    {index + 1}
                  </span>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-bold text-ink truncate">{broker.name}</p>
                    <p className="text-xs text-brand-600 font-black">{formatter.format(broker.productionVgv)}</p>
                  </div>
                  <IconChevronRight size={18} className={cn("transition-transform", selectedBrokerId === broker.id ? "text-brand-600 translate-x-1" : "text-gray-300")} />
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* DETAILS */}
        <div className="lg:col-span-2 space-y-6">
          {!selectedBrokerId ? (
            <div className="flex h-[400px] flex-col items-center justify-center rounded-3xl border-2 border-dashed border-border bg-white/40 text-center p-8 backdrop-blur-sm">
              <div className="h-16 w-16 rounded-2xl bg-brand-50 flex items-center justify-center text-brand-300 mb-6">
                <IconFilter size={32} />
              </div>
              <h3 className="text-lg font-bold text-ink mb-2">Selecione um corretor</h3>
              <p className="text-ink-muted text-sm max-w-xs leading-relaxed">
                Escolha um corretor na lista ao lado para ver o detalhamento do funil e resultados consolidados.
              </p>
            </div>
          ) : isPerformanceLoading ? (
            <div className="p-8 text-center text-ink-soft animate-pulse font-bold italic">Carregando detalhes...</div>
          ) : performance ? (
            <div className="space-y-6 animate-in fade-in slide-in-from-bottom-4 duration-500">
              {/* PERFORMANCE KPIs */}
              <div className="grid gap-6 md:grid-cols-2">
                <div className="rounded-3xl border border-white/40 bg-white/70 p-6 shadow-card backdrop-blur-sm flex items-center gap-5">
                  <div className="h-12 w-12 rounded-2xl bg-success-soft flex items-center justify-center text-success shadow-inner-light">
                    <IconBusinessplan size={24} />
                  </div>
                  <div>
                    <p className="text-[10px] font-black uppercase tracking-widest text-ink-soft">VGV Fechado</p>
                    <p className="text-2xl font-black text-ink">{formatter.format(performance.closedDealsValue)}</p>
                  </div>
                </div>
                <div className="rounded-3xl border border-white/40 bg-white/70 p-6 shadow-card backdrop-blur-sm flex items-center gap-5">
                  <div className="h-12 w-12 rounded-2xl bg-brand-50 flex items-center justify-center text-brand-600 shadow-inner-light">
                    <IconCash size={24} />
                  </div>
                  <div>
                    <p className="text-[10px] font-black uppercase tracking-widest text-ink-soft">Comissões</p>
                    <p className="text-2xl font-black text-ink">{formatter.format(performance.commissionsEarned)}</p>
                  </div>
                </div>
              </div>

              {/* SALES FUNNEL */}
              <div className="rounded-3xl border border-white/40 bg-white/70 shadow-card backdrop-blur-sm overflow-hidden">
                <div className="border-b border-border-soft bg-gray-50/30 px-8 py-5">
                  <h3 className="font-bold text-ink">Funil de Vendas Individual</h3>
                </div>
                <div className="p-8">
                  {performance.funnelData.every((d) => d.count === 0) ? (
                    <p className="py-8 text-center text-sm text-ink-muted">
                      Sem movimentação deste corretor no período.
                    </p>
                  ) : (
                  <div className="space-y-8">
                    {performance.funnelData.map((item, index) => {
                      // Guarda o divisor: com funil zerado o max seria 0 (ou -Infinity com
                      // lista vazia) e a barra ganhava width NaN%.
                      const maxCount = Math.max(1, ...performance.funnelData.map(d => d.count));
                      const widthPercentage = (item.count / maxCount) * 100;
                      
                      return (
                        <div key={item.stage} className="space-y-2.5">
                          <div className="flex justify-between text-sm">
                            <span className="font-bold text-ink-muted">{item.stage}</span>
                            <span className="font-black text-ink">{item.count}</span>
                          </div>
                          <div className="h-4 w-full bg-gray-100/50 rounded-full overflow-hidden shadow-inner-light">
                            <div 
                              className={cn(
                                "h-full transition-all duration-1000 ease-out shadow-glow",
                                index === 0 ? "bg-argon-grad" :
                                index === 1 ? "bg-brand-grad" :
                                index === 2 ? "bg-argon-grad-cool" :
                                "bg-argon-grad-warm"
                              )}
                              style={{ width: `${widthPercentage}%` }}
                            />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  )}
                </div>
              </div>

              {/* MORE STATS PLACEHOLDER */}
              <div className="rounded-2xl border-2 border-dashed border-border bg-white/20 p-6 text-center">
                <span className="text-ink-soft text-xs font-bold uppercase tracking-widest italic">
                  Gráficos de evolução temporal e metas (Em breve)
                </span>
              </div>
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
}
